import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronDown,
  ClipboardList,
  Info,
  Phone,
  AlertCircle,
  Share2,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL, ENTITY_TYPE_SELLER_OUTLET } from "utils/constants/others";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  formatInvoiceAmount,
  formatScheduleGroupNumber,
  formatSlotDate,
  formatWeight,
} from "./planShipmentUtils";
import {
  AddInvoicesButton,
  BackButton,
  CtaBar,
  DateHeader,
  DateLabel,
  DateList,
  DateSection,
  ExistingScheduleBanner,
  Header,
  HeaderTitle,
  InfoBanner,
  ModalButtonRow,
  ModalCancelButton,
  ModalConfirmButton,
  ModalDetailLabel,
  ModalDetailList,
  ModalDetailRow,
  ModalDetailValue,
  ModalHandle,
  ModalInfoCircle,
  ModalOverlay,
  ModalSheet,
  ModalTitle,
  NoSlotsCard,
  NoSlotsSubtitle,
  NoSlotsTitle,
  POCButton,
  PageFrame,
  PageShell,
  ScheduleButton,
  ScheduleExistsLabel,
  ScrollArea,
  SectionTitle,
  ShowOtherSlotsToggle,
  SkeletonBlock,
  SkeletonLine,
  SkeletonPadding,
  SlotButton,
  SlotGrid,
  SlotRadio,
  SlotTime,
  SlotsFullBadge,
  SuccessCtaBar,
  SuccessCtaRow,
  SuccessDetailLabel,
  SuccessDetailList,
  SuccessDetailRow,
  SuccessDetailValue,
  SuccessFrame,
  SuccessHeadline,
  SuccessHeader,
  SuccessIconCircle,
  SuccessScheduleNumber,
  SuccessScrollArea,
  SuccessShareButton,
  SuccessShell,
  SummaryBar,
  SummaryText,
  ViewScheduleButton,
  WarningIconCircle,
  ModalIconWrapper,
} from "./styles.slotSelection";

const isDateFull = (dateObj) =>
  dateObj.slots.every((slot) => Number(slot.available_capacity) === 0);

// Formats "09am - 01pm" → ["09", <span style small>am</span>, " - ", "01", <span style small>pm</span>]
const formatSlotTime = (displayName) => {
  if (!displayName) return "—";
  return displayName
    .split(/(am|pm)/i)
    .map((part, i) =>
      /^(am|pm)$/i.test(part) ? (
        <span key={i} style={{ fontSize: "14px" }}>
          {part.toLowerCase()}
        </span>
      ) : (
        part
      )
    );
};

const SlotsSkeleton = () => (
  <DateList>
    {[0, 1].map((i) => (
      <SkeletonBlock key={i}>
        <SkeletonPadding>
          <SkeletonLine $width="140px" $height="22px" />
          <SkeletonLine $height="46px" />
          <SkeletonLine $height="46px" />
        </SkeletonPadding>
      </SkeletonBlock>
    ))}
  </DateList>
);

const SlotSelection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const {
    getPOScheduleSlots,
    getPOScheduleSlotsStatus,
    poScheduleSlotDates,
    poScheduleSlotsNoAvailable,
    poScheduleSlotsExceedWeight,
    getPOScheduleSlotsError,
    createPOScheduleGroup,
    createPOScheduleGroupStatus,
    createdScheduleGroupId,
    createdScheduleDetails,
    createPOScheduleGroupError,
    resetCreatePOScheduleGroup,
    updatePOScheduleGroup,
    updatePOScheduleGroupStatus,
    updatedScheduleGroupId,
    updatedScheduleDetails,
    updatePOScheduleGroupError,
    resetUpdatePOScheduleGroup,
    planShipmentAsnConfig,
    getPlanShipmentHelpSections,
  } = usePlanShipmentStore();

  const {
    selectedPoIds = [],
    poInputs = {},
    poMeta = {},
    isEdit = false,
    scheduleGroupId: editScheduleGroupId = null,
    scheduleGroupDeliveryDate = null,
  } = state || {};

  // Derived summary values
  const totalWeight = selectedPoIds.reduce(
    (sum, id) => sum + (Number(poInputs[id]?.netWeight) || 0),
    0
  );
  const totalSkus = selectedPoIds.reduce(
    (sum, id) => sum + (Number(poInputs[id]?.numSkus) || 0),
    0
  );

  

  const warehouseName = poMeta[selectedPoIds[0]]?.warehouseName || "";
  const warehouseCommaIndex = warehouseName.indexOf(",");
  const warehouseCode = warehouseCommaIndex === -1 ? warehouseName : warehouseName.slice(0, warehouseCommaIndex).trim();
  const warehouseCity = warehouseCommaIndex === -1 ? "" : warehouseName.slice(warehouseCommaIndex + 1).trim();
  const warehouseDisplay = warehouseName
    ? (warehouseCity ? (
        <>{warehouseCode}<br />{warehouseCity}</>
      ) : warehouseCode)
    : "—";

  // PO with highest entered weight → phone for POC
  const pocPhone =
    [...selectedPoIds]
      .map((id) => ({
        weight: Number(poInputs[id]?.netWeight) || 0,
        phone: poMeta[id]?.phone || "",
      }))
      .sort((a, b) => b.weight - a.weight)[0]?.phone || "";

  const totalAmount = selectedPoIds.reduce(
    (sum, id) => sum + (Number(poMeta[id]?.amount) || 0),
    0
  );

  const [selectedSlotKey, setSelectedSlotKey] = useState(null);
  const [showOtherSlots, setShowOtherSlots] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Reset any stale schedule creation/update status on mount so we never
  // immediately land on the success/confirmation screen when re-entering this page.
  useEffect(() => {
    resetCreatePOScheduleGroup();
    resetUpdatePOScheduleGroup();
    const vendorIdLocal = window.localStorage.getItem("outlet_id");
    if (!planShipmentAsnConfig && vendorIdLocal) {
      getPlanShipmentHelpSections({ seller_outlet_id: vendorIdLocal });
    }
  }, []);

  // Fetch slots on mount
  useEffect(() => {
    if (selectedPoIds.length === 0) return;
    const vendorIdLocal = window.localStorage.getItem("outlet_id");
    getPOScheduleSlots({
      purchase_order_ids: selectedPoIds,
      required_capacity: totalWeight,
      entity_id: vendorIdLocal,
      entity_type: ENTITY_TYPE_SELLER_OUTLET,
    });
  }, []);

  const selectedSlotObj = poScheduleSlotDates
    .flatMap((d) => d.slots.map((s) => ({ ...s, date: d.date })))
    .find((s) => s.slot_key === selectedSlotKey);

  // When the selected slot already has a schedule group, the confirmation
  // screen should show totals for the fully-merged result, not just the
  // newly-selected POs.
  const { displayPoCount, displayWeight } = (() => {
    const existingGroup = !isEdit ? (selectedSlotObj?.schedule_group || null) : null;
    if (!existingGroup) {
      return { displayPoCount: selectedPoIds.length, displayWeight: totalWeight };
    }

    const existingPOs = Array.isArray(existingGroup.purchase_orders)
      ? existingGroup.purchase_orders
      : [];

    // Build a set of new PO ids for overlap detection
    const newPoIdSet = new Set(selectedPoIds.map(Number));

    // Start with the existing POs and track merged weight
    let mergedWeight = 0;
    const mergedPoIds = new Set();

    existingPOs.forEach((po) => {
      const numId = Number(po.purchase_order_id);
      mergedPoIds.add(numId);
      if (newPoIdSet.has(numId)) {
        // Overlapping PO: existing scheduled weight + new entered weight
        mergedWeight +=
          (Number(po.scheduled_weight_in_kg) || 0) +
          (Number(poInputs[numId]?.netWeight) || 0);
      } else {
        // Existing-only PO: carry through its scheduled weight
        mergedWeight += Number(po.scheduled_weight_in_kg) || 0;
      }
    });

    // Add POs that are new (not already in the existing group)
    selectedPoIds.forEach((id) => {
      const numId = Number(id);
      if (!mergedPoIds.has(numId)) {
        mergedPoIds.add(numId);
        mergedWeight += Number(poInputs[id]?.netWeight) || 0;
      }
    });

    return { displayPoCount: mergedPoIds.size, displayWeight: mergedWeight };
  })();

  // The schedule_group attached to the selected slot (only present when creating, not editing)
  const selectedSlotScheduleGroup = !isEdit ? (selectedSlotObj?.schedule_group || null) : null;

  const handleConfirmSchedule = () => {
    if (!selectedSlotObj) return;

    // The backend expects schedule_date as a full RFC3339 timestamp
    const scheduleDate = selectedSlotObj.date
      ? `${selectedSlotObj.date.slice(0, 10)}T00:00:00Z`
      : selectedSlotObj.date;

    const scheduleDetails = {
      date: selectedSlotObj.date,
      display_name: selectedSlotObj.display_name,
    };

    if (isEdit) {
      // Plain edit — send exactly the POs the user came in with
      const poSchedules = selectedPoIds.map((id) => ({
        purchase_order_id: Number(id),
        purchase_order_number: poMeta[id]?.purchaseOrderNumber || "",
        tonnage: Number(poInputs[id]?.netWeight) || 0,
        number_of_skus: Number(poInputs[id]?.numSkus) || 0,
      }));
      updatePOScheduleGroup(
        {
          schedule_group_id: editScheduleGroupId,
          po_schedules: poSchedules,
          slot_id: selectedSlotObj.slot_id,
          schedule_date: scheduleDate,
        },
        scheduleDetails
      );
    } else if (selectedSlotScheduleGroup) {
      // Slot already has a schedule group — merge POs and update that group
      const existingPOs = Array.isArray(selectedSlotScheduleGroup.purchase_orders)
        ? selectedSlotScheduleGroup.purchase_orders
        : [];

      // Build a map of existing POs keyed by purchase_order_id
      const existingPoMap = {};
      existingPOs.forEach((po) => {
        existingPoMap[Number(po.purchase_order_id)] = po;
      });

      // Start with the newly selected POs
      const mergedMap = {};
      selectedPoIds.forEach((id) => {
        const numId = Number(id);
        const newTonnage = Number(poInputs[id]?.netWeight) || 0;
        const newSkus = Number(poInputs[id]?.numSkus) || 0;
        const existing = existingPoMap[numId];
        if (existing) {
          // PO already in the schedule: add tonnage, take max skus
          mergedMap[numId] = {
            purchase_order_id: numId,
            purchase_order_number: poMeta[id]?.purchaseOrderNumber || existing.purchase_order_number || "",
            tonnage: (Number(existing.scheduled_weight_in_kg) || 0) + newTonnage,
            number_of_skus: Math.max(Number(existing.scheduled_sku_count) || 0, newSkus),
          };
        } else {
          mergedMap[numId] = {
            purchase_order_id: numId,
            purchase_order_number: poMeta[id]?.purchaseOrderNumber || "",
            tonnage: newTonnage,
            number_of_skus: newSkus,
          };
        }
      });

      // Also keep existing POs that are NOT in the newly selected set
      existingPOs.forEach((po) => {
        const numId = Number(po.purchase_order_id);
        if (!mergedMap[numId]) {
          mergedMap[numId] = {
            purchase_order_id: numId,
            purchase_order_number: po.purchase_order_number || "",
            tonnage: Number(po.scheduled_weight_in_kg) || 0,
            number_of_skus: Number(po.scheduled_sku_count) || 0,
          };
        }
      });

      updatePOScheduleGroup(
        {
          schedule_group_id: selectedSlotScheduleGroup.schedule_group_id,
          po_schedules: Object.values(mergedMap),
          slot_id: selectedSlotObj.slot_id,
          schedule_date: scheduleDate,
        },
        scheduleDetails
      );
    } else {
      // Fresh create
      const poSchedules = selectedPoIds.map((id) => ({
        purchase_order_id: Number(id),
        purchase_order_number: poMeta[id]?.purchaseOrderNumber || "",
        tonnage: Number(poInputs[id]?.netWeight) || 0,
        number_of_skus: Number(poInputs[id]?.numSkus) || 0,
      }));
      createPOScheduleGroup(
        {
          po_schedules: poSchedules,
          slot_id: selectedSlotObj.slot_id,
          schedule_date: scheduleDate,
        },
        scheduleDetails
      );
    }
  };

  const isUpdatingExisting = isEdit || Boolean(selectedSlotScheduleGroup);
  const isCreating = isUpdatingExisting
    ? updatePOScheduleGroupStatus === STATUS_BEGIN
    : createPOScheduleGroupStatus === STATUS_BEGIN;
  const isSuccess = isUpdatingExisting
    ? updatePOScheduleGroupStatus === STATUS_DONE
    : createPOScheduleGroupStatus === STATUS_DONE;
  const resultScheduleGroupId = isEdit
    ? editScheduleGroupId
    : (isUpdatingExisting ? (selectedSlotScheduleGroup?.schedule_group_id || updatedScheduleGroupId) : createdScheduleGroupId);
  const resultScheduleDetails = isUpdatingExisting ? updatedScheduleDetails : createdScheduleDetails;
  const scheduleError = isUpdatingExisting ? updatePOScheduleGroupError : createPOScheduleGroupError;

 
  const eddAllowedDates = (() => {
    const referenceEdd = isEdit
      ? scheduleGroupDeliveryDate
      : poMeta[selectedPoIds[0]]?.edd;
    if (!referenceEdd) return new Set();
    const base = new Date(referenceEdd);
    if (isNaN(base.getTime())) return new Set();
    base.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const toLocalDateStr = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const dateSet = new Set();
    [-1, 0, 1].forEach((offset) => {
      const d = new Date(base);
      d.setDate(d.getDate() + offset);
      dateSet.add(toLocalDateStr(d));
    });
    return dateSet;
  })();

  // Filter slot dates to only EDD±1 window; fall back to all dates if no EDD info
  const eddFilteredDates =
    eddAllowedDates.size > 0
      ? poScheduleSlotDates.filter((d) => eddAllowedDates.has(d.date?.slice(0, 10)))
      : poScheduleSlotDates;

  const allottedDates = eddFilteredDates.filter((d) => d.is_allotted);
  const hasOtherDates =
    eddFilteredDates.length > allottedDates.length && allottedDates.length > 0;
  const visibleDates =
    showOtherSlots || allottedDates.length === 0
      ? eddFilteredDates
      : allottedDates;

  const hasAnyBlocker = poScheduleSlotsNoAvailable || poScheduleSlotsExceedWeight;
  const isLoading =
    getPOScheduleSlotsStatus === STATUS_BEGIN || !getPOScheduleSlotsStatus;

  const summaryParts = [
    `${displayPoCount} ${displayPoCount === 1 ? "PO" : "POs"}`,
    formatWeight(displayWeight),
    `${totalSkus} SKUs`,
    warehouseCode || warehouseName,
  ].filter(Boolean);

  if (isSuccess && resultScheduleGroupId) {
    const scheduleNumber = formatScheduleGroupNumber(resultScheduleDetails || {});
    const scheduleUrl = `${window.location.origin}/plan-shipment/schedule-groups/${resultScheduleGroupId}`;

    const handleShare = async () => {
      try {
        if (navigator.share) {
          await navigator.share({
            title: scheduleNumber,
            text: `Schedule ${scheduleNumber}`,
            url: scheduleUrl,
          });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(scheduleUrl);
        }
      } catch (error) {
        if (error?.name !== "AbortError") console.error(error);
      }
    };

    const handleNavigateToSchedule = () => {
      if (isUpdatingExisting) {
        resetUpdatePOScheduleGroup();
      } else {
        resetCreatePOScheduleGroup();
      }
      navigate(`/plan-shipment/schedule-groups/${resultScheduleGroupId}`);
    };

    const handleNavigateToScheduleList = () => {
      if (isUpdatingExisting) {
        resetUpdatePOScheduleGroup();
      } else {
        resetCreatePOScheduleGroup();
      }
      navigate("/plan-shipment", { state: { activeScheduleTab: "scheduled" } });
    };

    return (
      <SuccessShell>
        <SuccessFrame>
          <SuccessHeader>
            <HeaderTitle>{isUpdatingExisting ? "Schedule updated" : "Schedule created"}</HeaderTitle>
            <SuccessShareButton
              type="button"
              onClick={handleShare}
              aria-label="Share schedule"
            >
              <Share2 aria-hidden="true" />
            </SuccessShareButton>
          </SuccessHeader>
          <SuccessScrollArea>
            <SuccessIconCircle>
              <img src="/logos/tick_logo.svg" alt="" aria-hidden="true" />
            </SuccessIconCircle>
            <SuccessHeadline>{isUpdatingExisting ? "Schedule updated" : "Schedule created"}</SuccessHeadline>
            {scheduleNumber ? (
              <SuccessScheduleNumber>
                {scheduleNumber}
              </SuccessScheduleNumber>
            ) : null}
            <SuccessDetailList>
              <SuccessDetailRow>
                <SuccessDetailLabel>Delivery location</SuccessDetailLabel>
                <SuccessDetailValue>{warehouseDisplay}</SuccessDetailValue>
              </SuccessDetailRow>
              <SuccessDetailRow>
                <SuccessDetailLabel>Delivery date</SuccessDetailLabel>
                <SuccessDetailValue>
                  {formatSlotDate(resultScheduleDetails?.date) || "—"}
                </SuccessDetailValue>
              </SuccessDetailRow>
              <SuccessDetailRow>
                <SuccessDetailLabel>Reporting time</SuccessDetailLabel>
                <SuccessDetailValue>
                  {resultScheduleDetails?.display_name || "—"}
                </SuccessDetailValue>
              </SuccessDetailRow>
              <SuccessDetailRow>
                <SuccessDetailLabel>POs</SuccessDetailLabel>
                <SuccessDetailValue>{displayPoCount}</SuccessDetailValue>
              </SuccessDetailRow>
              <SuccessDetailRow>
                <SuccessDetailLabel>Net weight</SuccessDetailLabel>
                <SuccessDetailValue>{formatWeight(displayWeight)}</SuccessDetailValue>
              </SuccessDetailRow>
              <SuccessDetailRow>
                <SuccessDetailLabel>Total amount</SuccessDetailLabel>
                <SuccessDetailValue>
                  {formatInvoiceAmount(totalAmount)}
                </SuccessDetailValue>
              </SuccessDetailRow>
            </SuccessDetailList>
          </SuccessScrollArea>
          <SuccessCtaBar>
            <SuccessCtaRow>
              <AddInvoicesButton
                type="button"
                onClick={handleNavigateToSchedule}
              >
                Add invoices
              </AddInvoicesButton>
              <ViewScheduleButton
                type="button"
                onClick={handleNavigateToScheduleList}
              >
                View schedules
              </ViewScheduleButton>
            </SuccessCtaRow>
          </SuccessCtaBar>
        </SuccessFrame>
      </SuccessShell>
    );
  }

  return (
    <PageShell>
      <PageFrame>
        <Header>
          <BackButton
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </BackButton>
          <HeaderTitle>Schedule POs</HeaderTitle>
        </Header>

        <SummaryBar as="div">
          <ClipboardList aria-hidden="true" />
          <SummaryText>
            {summaryParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && " • "}
                {i < 2 ? <strong>{part}</strong> : part}
              </React.Fragment>
            ))}
          </SummaryText>
        </SummaryBar>

        <ScrollArea>
          <SectionTitle>Select reporting time</SectionTitle>

          <InfoBanner>
            <Info aria-hidden="true" />
            Reporting after 4 PM will not be accepted
          </InfoBanner>

          {isLoading ? <SlotsSkeleton /> : null}

          {!isLoading && getPOScheduleSlotsStatus === STATUS_FAIL ? (
            <NoSlotsCard>
              <WarningIconCircle>!</WarningIconCircle>
              <NoSlotsTitle>Unable to load slots</NoSlotsTitle>
              <NoSlotsSubtitle>{getPOScheduleSlotsError}</NoSlotsSubtitle>
              <POCButton
                as="button"
                type="button"
                onClick={() =>
                  getPOScheduleSlots({
                    purchase_order_ids: selectedPoIds,
                    required_capacity: totalWeight,
                    entity_id: window.localStorage.getItem("outlet_id"),
                    entity_type: ENTITY_TYPE_SELLER_OUTLET,
                  })
                }
              >
                Try again
              </POCButton>
            </NoSlotsCard>
          ) : null}

          {getPOScheduleSlotsStatus === STATUS_DONE ? (
            <>
              {hasAnyBlocker ? (
                <NoSlotsCard>
                  <WarningIconCircle>!</WarningIconCircle>
                  <NoSlotsTitle>
                    {poScheduleSlotsExceedWeight
                      ? `Scheduling > ${
                          Number(planShipmentAsnConfig?.slot_full_schedule_creation_max_limit_in_kg) > 0
                            ? Number(planShipmentAsnConfig.slot_full_schedule_creation_max_limit_in_kg) / 1000
                            : 5
                        } tonnes`
                      : "No slots available"}
                  </NoSlotsTitle>
                  <NoSlotsSubtitle>
                    Call your category manager to get help
                  </NoSlotsSubtitle>
                  {pocPhone ? (
                    <POCButton href={`tel:${pocPhone}`}>
                      <Phone aria-hidden="true" />
                      Call Hyperpure POC
                    </POCButton>
                  ) : (
                    <POCButton as="button" type="button" disabled>
                      <Phone aria-hidden="true" />
                      Call Hyperpure POC
                    </POCButton>
                  )}
                </NoSlotsCard>
              ) : null}

              <DateList>
                {visibleDates.map((dateObj) => {
                  const dateFull = isDateFull(dateObj);
                  return (
                    <DateSection key={dateObj.date}>
                      <DateHeader>
                        <DateLabel>{formatSlotDate(dateObj.date)}</DateLabel>
                        {dateFull ? (
                          <SlotsFullBadge>Slots full</SlotsFullBadge>
                        ) : null}
                      </DateHeader>

                      {!dateFull ? (
                        <>
                          <SlotGrid>
                            {dateObj.slots.map((slot) => {
                              const isFull =
                                Number(slot.available_capacity) === 0;
                              const isSelected =
                                selectedSlotKey === slot.slot_key;
                              const hasExistingSchedule =
                                !isEdit && Boolean(slot.schedule_group);
                              return (
                                <SlotButton
                                  key={slot.slot_key}
                                  type="button"
                                  $selected={isSelected}
                                  $disabled={isFull}
                                  disabled={isFull}
                                  onClick={() =>
                                    !isFull &&
                                    setSelectedSlotKey((prev) =>
                                      prev === slot.slot_key ? null : slot.slot_key
                                    )
                                  }
                                  aria-pressed={isSelected}
                                  style={{ flexDirection: "column", alignItems: "flex-start", gap: 2 }}
                                >
                                  <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                                    <SlotTime>
                                      {formatSlotTime(slot.display_name)}
                                    </SlotTime>
                                    <SlotRadio $selected={isSelected} />
                                  </div>
                                  {hasExistingSchedule ? (
                                    <ScheduleExistsLabel>
                                      Schedule exists
                                    </ScheduleExistsLabel>
                                  ) : null}
                                </SlotButton>
                              );
                            })}
                          </SlotGrid>
                          {selectedSlotScheduleGroup &&
                          dateObj.slots.some((s) => s.slot_key === selectedSlotKey) ? (
                            <ExistingScheduleBanner>
                              <Info aria-hidden="true" />
                              Existing schedule will be updated
                            </ExistingScheduleBanner>
                          ) : null}
                        </>
                      ) : null}
                    </DateSection>
                  );
                })}
              </DateList>

              {hasOtherDates ? (
                <ShowOtherSlotsToggle
                  type="button"
                  $open={showOtherSlots}
                  onClick={() => setShowOtherSlots((prev) => !prev)}
                >
                  {showOtherSlots ? "Hide other slots" : "Show other slots"}
                  <ChevronDown aria-hidden="true" />
                </ShowOtherSlotsToggle>
              ) : null}
            </>
          ) : null}
        </ScrollArea>

        <CtaBar>
          <ScheduleButton
            type="button"
            disabled={hasAnyBlocker || !selectedSlotKey}
            onClick={() => setShowConfirmModal(true)}
          >
            Schedule POs
          </ScheduleButton>
        </CtaBar>
      </PageFrame>

      {showConfirmModal ? (
        <ModalOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowConfirmModal(false);
          }}
        >
          <ModalSheet>
            <ModalHandle />
            <ModalIconWrapper>
              <img src="/logos/info-line.svg" alt="" aria-hidden="true" style={{ width: 48, height: 48 }} />
            </ModalIconWrapper>
            <ModalTitle>Confirm details</ModalTitle>
            <ModalDetailList>
              <ModalDetailRow>
                <ModalDetailLabel>Delivery location</ModalDetailLabel>
                <ModalDetailValue>{warehouseDisplay}</ModalDetailValue>
              </ModalDetailRow>
              <ModalDetailRow>
                <ModalDetailLabel>Delivery date</ModalDetailLabel>
                <ModalDetailValue>
                  {formatSlotDate(selectedSlotObj?.date)}
                </ModalDetailValue>
              </ModalDetailRow>
              <ModalDetailRow>
                <ModalDetailLabel>Reporting time</ModalDetailLabel>
                <ModalDetailValue>
                  {selectedSlotObj?.display_name || "—"}
                </ModalDetailValue>
              </ModalDetailRow>
              <ModalDetailRow>
                <ModalDetailLabel>POs</ModalDetailLabel>
                <ModalDetailValue>{displayPoCount}</ModalDetailValue>
              </ModalDetailRow>
              <ModalDetailRow>
                <ModalDetailLabel>Net weight</ModalDetailLabel>
                <ModalDetailValue>{formatWeight(displayWeight)}</ModalDetailValue>
              </ModalDetailRow>
              <ModalDetailRow>
                <ModalDetailLabel>Total amount</ModalDetailLabel>
                <ModalDetailValue>
                  {formatInvoiceAmount(totalAmount)}
                </ModalDetailValue>
              </ModalDetailRow>
            </ModalDetailList>
            {(isEdit ? updatePOScheduleGroupStatus : createPOScheduleGroupStatus) === STATUS_FAIL ? (
              <p style={{ margin: "0 0 16px", color: "#ef4f5f", fontSize: "14px", textAlign: "center" }}>
                {scheduleError}
              </p>
            ) : null}
            <ModalButtonRow>
              <ModalCancelButton
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isCreating}
              >
                Cancel
              </ModalCancelButton>
              <ModalConfirmButton
                type="button"
                disabled={isCreating}
                onClick={handleConfirmSchedule}
              >
                {isCreating ? "Scheduling…" : "Yes, schedule"}
              </ModalConfirmButton>
            </ModalButtonRow>
          </ModalSheet>
        </ModalOverlay>
      ) : null}
    </PageShell>
  );
};

export default SlotSelection;
